import { QueryParameter, Mapping } from "Core";
import { IBaseGrid } from "Grids/Interfaces";
import { CoreContext } from "Core/Core";
import { MappingDisplayType, GridTypes } from "Enums";

/**
 * ## Parent Child
 * 
 * Methods available when the pageMode is ParentChild. 
 * @since 4.0.0
 * @example 
 * ```JavaScript
 * // load mapping in the child part
 * DMS.ParentChild.loadChild('MappingName', 'editMode=add');
 * 
 * // clear child content
 * DMS.ParentChild.clearChild();
 * ```
 */
export class ParentChild extends CoreContext {

    private container: JQuery;
    // private parentContainer: JQuery;
    private childContainer: JQuery;
    private childHeader: JQuery;

    private splitter: kendo.ui.Splitter;

    private childGrids: IBaseGrid[];

    private childMapping: Mapping;

    /**
     * @hidden
     */
    public isParentChild: boolean = true;

    /**
     * Create a new {@link ParentChild}.
     * @hidden
     */
    constructor() {
        super();
        this.childGrids = [];
    }

    /**
     * Initialize the {@link ParentChild}.
     * @param pageToken The pageToken created on the server.
     * @param puiTimeout PageToken Timeout.
     * @param textBlockCategory TextBlockCategory to use for textblocks.
     * @hidden
     */
    public initialize(pageToken: string, puiTimeout: number, textBlockCategory: string): void {
        super.initialize(pageToken, puiTimeout, textBlockCategory);

        this.container = $(".dms-parent-child");
        // this.parentContainer = this.container.find(".dms-parent");
        this.childContainer = this.container.find(".dms-child");

        this.splitter = new kendo.ui.Splitter(this.container[0], {
            panes: [{
                collapsible: true,
                size: QueryParameter.get("leftpanewidth"),
                scrollable: false
            }, {
            }]
        });
    }

    /**
     * Resize ParentChild
     * @hidden
     */
    public resizePage(): void {
        let height = this.getAvailableHeight();
        this.container.height(height - 1);
        if (this.mapping) {
            this.mapping.setHeight(height - 2);
        }
        if (this.iframe) {
            this.iframe.height(height - 5);
        } else {
            let childHeaderHeight = 0;
            if (this.childHeader) {
                childHeaderHeight = this.childHeader.outerHeight(true);
            }

            if (this.childMapping) {
                this.childMapping.setHeight(height - childHeaderHeight - 2);
            }
        }
        this.splitter.resize();
    }

    /**
     * Get the current instance for ParentChild.
     * @hidden
     */
    public static get Instance(): ParentChild {
        return <ParentChild>CoreContext.Instance;
    }

    /**
     * Initialize the parent child instance.
     * @hidden
     */
    public static initialize() {
        let instance = new ParentChild();
        CoreContext.Instance = instance;
    }

    /**
     * Clear the content of the child container.
     * @hidden
     */
    public clearChild(): void {
        // dispose all child grids.
        for (let i = 0, len = this.childGrids.length; i < len; i++) {
            let grid = this.childGrids[i];
            grid.dispose();
        }

        if (this.childMapping) {
            this.childMapping.dispose();
        }

        if (this.iframe) {
            this.iframe = null;
        }
        this.childGrids = [];
        this.childContainer.empty();
        QueryParameter.initialize(); // reset query parameters to querystring only.
    }

    /**
     * Reload all grids in the child container.
     * @hidden
     */
    public reloadChild(): void {
        // reload all child grids.
        for (let i = 0, len = this.childGrids.length; i < len; i++) {
            let grid = this.childGrids[i];
            if (grid.getGridType() != GridTypes.Filter) {
                grid.reload();
            }
        }
    }

    /**
     * Reload all grids in the child container.
     * @since 4.0.0
     */
    public reloadParent(): void {
        if (this.mapping) {
            this.mapping.reload();
        }
    }

    /**
     * Get all grids of this context (parent and child-grids).
     * @hidden
     */
    public getGrids(): Array<IBaseGrid> {
        let grids = super.getGrids();
        if (this.childMapping) {
            let childGrids = this.childMapping.getGrids();
            if (childGrids && childGrids.length > 0) {
                grids = grids.concat(childGrids);
            }
        }
        return grids;
    }

    /**
     * Load a mapping into the child container.
     * @param mappingName Name of the mapping to load.
     * @hidden
     */
    public async loadChild(mappingName: string): Promise<void> {
        let useChildTextBlocks = QueryParameter.get("tbm") != "";
        let textBlockCategory = QueryParameter.get("tbm") || mappingName;
        let textblocks = await this.serviceProvider.TextBlock.load(this._environment.LanguageCode, textBlockCategory);
        let pageTitle: string;
        if (useChildTextBlocks) {
            this.currentTextBlockCategory = textBlockCategory;
            let tbs = this.pageViewModel.get("TextBlock");
            this.pageViewModel.set("TextBlock", $.extend(tbs, textblocks));
            pageTitle = this.serviceProvider.TextBlock.get("pageTitle", textBlockCategory);
        } else {
            this.currentTextBlockCategory = this.mapping.getMappingName(); // reset the category to the original mapping name (=parent mapping)
            pageTitle = this.serviceProvider.TextBlock.getChecked(textblocks[mappingName], "pageTitle", mappingName);
        }

        this.childHeader = $(`<div class='dms-parent-child-header'><h3>${pageTitle}</h3></div>`);
        this.childContainer.append(this.childHeader);

        this.childMapping = new Mapping(mappingName, textBlockCategory, this.serviceProvider, this.globalInitializePromise);
        await this.childMapping.initialize(this.childContainer, MappingDisplayType.FilterAndGrids);
        // this.initializeGrids(this.childContainer, mappingName, this.childGrids, MappingDisplayType.FilterAndGrids, this.currentTextBlockCategory);
        this.resizePage();
    }

    private iframe: JQuery;

    public loadChildFromUrl(url: string): void {
        this.clearChild();

        this.iframe = $(`<iframe frameborder="0" style="overflow:hidden;overflow-x:hidden;overflow-y:scroll;border:0;width:100%;top:0px;left:0px;right:0px;bottom:0px" />`);
        this.childContainer.append(this.iframe);
        this.iframe.attr("src", url);
        this.resizePage();
    }

    /**
     * Load a mapping into the child container.
     * @param mappingName Name of the mapping to load.
     * @param params Additional parameters in querystring format.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * // load mapping in the child part
     * DMS.ParentChild.loadChild('MappingName', 'editMode=add');
     * ```
     */
    public static loadChild(mappingName: string, params: string): Promise<void> {
        this.clearChild();
        // reset query parameters
        QueryParameter.initialize(); 
        QueryParameter.addFromQueryString(params, false);
        return this.Instance.loadChild(mappingName);
    }

    /**
     * Load a mapping into the child container with a json string parameter.
     * @param mappingName Name of the mapping to load.
     * @param params Additional parameters in json format. The json property names must be in quotes.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * // load mapping in the child part
     * DMS.ParentChild.loadChildJson('MappingName', '{ "editMode": "add" }');
     * ```
     */
    public static loadChildJson(mappingName: string, params: string): Promise<void> {
        this.clearChild();
        // reset query parameters
        QueryParameter.clear();
        let object = JSON.parse(params);
        QueryParameter.addFromObject(object);
        return this.Instance.loadChild(mappingName);
    }

    /**
     * Clear the content of the child container.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * // clear child content
     * DMS.ParentChild.clearChild();
     * ```
     */
    public static clearChild(): void {
        this.Instance.clearChild();
    }

    /**
     * Reload the grids in the child container.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * DMS.ParentChild.reloadChild();
     * ```
     */
    public static reloadChild(): void {
        this.Instance.reloadChild();
    }

    /**
     * Returns true if the parent child mode is enabled and initialized.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.ParentChild.isPageModeParentChild(); // -> true;
     * ```
     */
    public static isPageModeParentChild(): boolean {
        return this.Instance.isParentChild === true;
    }

    /**
     * Reload the grids in the parent container.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * DMS.ParentChild.reloadParent();
     * ```
     */
    public static reloadParent(): void {
        this.Instance.reloadParent();
    }

    /**
     * Reload the grids in the parent and child containers.
     * @since 4.0.0
     * @example 
     * ```JavaScript
     * DMS.ParentChild.reloadParentChild();
     * ```
     */
    public static reloadParentChild(): void {
        this.Instance.reloadParent();
        this.Instance.reloadChild();
    }

    /**
     * Load a page from given url into the child container.
     * @param url Url to be loaded.
     * @since 4.0.0
     */
    public static loadChildFromUrl(url: string): void {
        this.Instance.loadChildFromUrl(url);
    }
}